/* =========================================================================
   ClueVine — Forge (letter-set word building)
   Seven letters, one required center. Spell as many valid words as you can
   (letters may repeat, every word must use the center). A pangram uses all
   seven. The target is sized to what's actually findable, so it's always
   reachable — and you can always Finish whenever you like.
   ========================================================================= */

import { pickAvoiding } from '../../engine/seed';
import type {
  DifficultyLevel,
  GenerateContext,
  HintOutcome,
  InputKey,
  MoveFeedback,
  ModeScore,
  PuzzleStatus,
  ResultSummary,
} from '../../engine/types';

export interface ForgeConfig {
  minLength: number;
  targetFraction: number;
}

export interface ForgeState {
  letters: string[]; // 7 letters; index 0 is the center
  center: string;
  minLength: number;
  validWords: string[];
  pangrams: string[];
  found: string[]; // newest first
  current: string;
  target: number; // words needed to "pass"
  maxPoints: number;
  finished: boolean;
}

export type ForgeMove =
  | { type: 'add'; ch: string }
  | { type: 'remove' }
  | { type: 'clear' }
  | { type: 'shuffle' }
  | { type: 'submit' }
  | { type: 'finish' };

export const FORGE_DIFFICULTIES: DifficultyLevel<ForgeConfig>[] = [
  { id: 'easy', label: 'Sprout', blurb: '4+ letters, gentle target', scoreMultiplier: 0.9, config: { minLength: 4, targetFraction: 0.25 } },
  { id: 'standard', label: 'Garden', blurb: '4+ letters', scoreMultiplier: 1, config: { minLength: 4, targetFraction: 0.4 } },
  { id: 'hard', label: 'Thicket', blurb: '5+ letters', scoreMultiplier: 1.3, config: { minLength: 5, targetFraction: 0.5 } },
];

export const FORGE_RANKS = ['Seed', 'Sprout', 'Sapling', 'Leaf', 'Bud', 'Bloom', 'Canopy'] as const;

const FALLBACK_SETS = ['aeginrt', 'adenrst', 'aceilrt', 'adeilnt', 'aelnrst'];

export function isPangram(word: string, letters: string[]): boolean {
  return letters.every((l) => word.includes(l));
}

export function wordPoints(word: string, letters: string[]): number {
  let pts = word.length <= 4 ? 1 : word.length;
  if (isPangram(word, letters)) pts += 7;
  return pts;
}

function distinctLetters(word: string): string[] {
  return [...new Set(word.split(''))];
}

/** Sorted unique-letter key — the stable identity used for avoid-recent. */
export function letterKey(letters: string[]): string {
  return [...letters].sort().join('');
}

export function generate(ctx: GenerateContext<ForgeConfig>): ForgeState {
  const { minLength, targetFraction } = ctx.difficulty.config;

  // Candidate letter-sets: 7-letter words with exactly 7 distinct letters.
  const seeds = ctx.dictionary.byLength(7).filter((w) => distinctLetters(w).length === 7);
  const keys = seeds.length > 0 ? [...new Set(seeds.map((w) => letterKey(w.split(''))))] : FALLBACK_SETS;

  const key = pickAvoiding(ctx.prng, keys, ctx.avoid);
  const setLetters = key.split('');

  // Choose the center that yields the most findable words (anti-frustration).
  let best = { center: setLetters[0], words: [] as string[] };
  for (const candidate of setLetters) {
    const words = ctx.dictionary.wordsFromLetters(setLetters, {
      required: candidate,
      minLength,
      allowRepeats: true,
    });
    if (words.length > best.words.length) best = { center: candidate, words };
  }

  const center = best.center;
  // Put the center first; shuffle the rest for visual variety.
  const outer = ctx.prng.shuffle(setLetters.filter((l) => l !== center));
  const letters = [center, ...outer];

  const validWords = [...best.words].sort();
  const pangrams = validWords.filter((w) => isPangram(w, letters));
  const maxPoints = validWords.reduce((sum, w) => sum + wordPoints(w, letters), 0);
  const target = Math.max(1, Math.min(validWords.length, 14, Math.ceil(validWords.length * targetFraction)));

  return {
    letters,
    center,
    minLength,
    validWords,
    pangrams,
    found: [],
    current: '',
    target,
    maxPoints,
    finished: false,
  };
}

export function status(state: ForgeState): PuzzleStatus {
  const allFound = state.found.length >= state.validWords.length && state.validWords.length > 0;
  if (state.finished || allFound) {
    return state.found.length > 0 ? 'won' : 'lost';
  }
  return 'playing';
}

export function validateMove(state: ForgeState, move: ForgeMove): { ok: boolean; reason?: string } {
  if (move.type !== 'submit') return { ok: true };
  const word = state.current;
  if (word.length < state.minLength) return { ok: false, reason: `At least ${state.minLength} letters` };
  if (!word.includes(state.center)) return { ok: false, reason: 'Must use the center letter' };
  for (const ch of word) {
    if (!state.letters.includes(ch)) return { ok: false, reason: 'Use only the given letters' };
  }
  if (state.found.includes(word)) return { ok: false, reason: 'Already found' };
  if (!state.validWords.includes(word)) return { ok: false, reason: 'Not in word list' };
  return { ok: true };
}

function rotate<T>(arr: T[], by: number): T[] {
  if (arr.length === 0) return arr;
  const k = ((by % arr.length) + arr.length) % arr.length;
  return [...arr.slice(k), ...arr.slice(0, k)];
}

export function reduce(state: ForgeState, move: ForgeMove): ForgeState {
  switch (move.type) {
    case 'add':
      if (state.current.length >= 9 || !state.letters.includes(move.ch)) return state;
      return { ...state, current: state.current + move.ch };
    case 'remove':
      return state.current ? { ...state, current: state.current.slice(0, -1) } : state;
    case 'clear':
      return state.current ? { ...state, current: '' } : state;
    case 'shuffle': {
      const outer = rotate(state.letters.slice(1), 2);
      return { ...state, letters: [state.letters[0], ...outer] };
    }
    case 'submit': {
      const word = state.current;
      if (!state.validWords.includes(word) || state.found.includes(word)) {
        return { ...state, current: '' };
      }
      return { ...state, found: [word, ...state.found], current: '' };
    }
    case 'finish':
      return { ...state, finished: true };
    default:
      return state;
  }
}

export function handleKey(state: ForgeState, key: InputKey): ForgeMove | null {
  if (key.type === 'char') {
    return state.letters.includes(key.value) ? { type: 'add', ch: key.value } : null;
  }
  if (key.type === 'backspace') {
    return state.current ? { type: 'remove' } : null;
  }
  return { type: 'submit' };
}

export function hint(state: ForgeState): HintOutcome<ForgeState> | null {
  if (status(state) !== 'playing') return null;
  const remaining = state.validWords.filter((w) => !state.found.includes(w));
  if (remaining.length === 0) return null;
  // Nudge toward the easiest remaining word.
  const target = [...remaining].sort((a, b) => a.length - b.length)[0];
  return {
    message: `Try a ${target.length}-letter word starting with “${target[0].toUpperCase()}”.`,
    penalty: 40,
  };
}

export function moveFeedback(prev: ForgeState, next: ForgeState): MoveFeedback | null {
  if (next.found.length > prev.found.length) {
    const word = next.found[0];
    const pts = wordPoints(word, next.letters);
    return isPangram(word, next.letters)
      ? { message: `Pangram! ${word.toUpperCase()} +${pts}`, tone: 'good' }
      : { message: `${word.toUpperCase()} +${pts}`, tone: 'good' };
  }
  return null;
}

export function foundPoints(state: ForgeState): number {
  return state.found.reduce((sum, w) => sum + wordPoints(w, state.letters), 0);
}

export function score(state: ForgeState): ModeScore {
  const base = foundPoints(state) * 12;
  return {
    base,
    lines: [{ label: `${state.found.length} words found`, value: base, kind: 'base' }],
    parMs: state.target * 14000,
    perfect: state.validWords.length > 0 && state.found.length === state.validWords.length,
  };
}

export function forgeRank(state: ForgeState): { label: string; index: number; fraction: number } {
  const fraction = state.maxPoints > 0 ? foundPoints(state) / state.maxPoints : 0;
  const index = Math.min(FORGE_RANKS.length - 1, Math.floor(fraction * FORGE_RANKS.length));
  return { label: FORGE_RANKS[index], index, fraction };
}

export function summarize(state: ForgeState): ResultSummary {
  const won = status(state) === 'won';
  const pangrams = state.found.filter((w) => isPangram(w, state.letters)).length;
  const grid = [`🌿 ${state.found.length}/${state.validWords.length} words`];
  if (pangrams > 0) grid.push(`🌸 ${pangrams} pangram${pangrams === 1 ? '' : 's'}`);
  return {
    status: won ? 'won' : 'lost',
    headline: won ? `${forgeRank(state).label} · ${state.found.length} words` : 'No words found',
    avoidKeys: [letterKey(state.letters)],
    detail: state.letters.join('').toUpperCase(),
    shareGrid: grid,
  };
}

export function deserialize(raw: unknown): ForgeState | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<ForgeState>;
  if (!Array.isArray(s.letters) || typeof s.center !== 'string') return null;
  if (!Array.isArray(s.validWords) || !Array.isArray(s.found)) return null;
  if (typeof s.current !== 'string' || typeof s.target !== 'number') return null;
  return {
    letters: s.letters.filter((l): l is string => typeof l === 'string'),
    center: s.center,
    minLength: typeof s.minLength === 'number' ? s.minLength : 4,
    validWords: s.validWords.filter((w): w is string => typeof w === 'string'),
    pangrams: Array.isArray(s.pangrams) ? s.pangrams.filter((w): w is string => typeof w === 'string') : [],
    found: s.found.filter((w): w is string => typeof w === 'string'),
    current: s.current,
    target: s.target,
    maxPoints: typeof s.maxPoints === 'number' ? s.maxPoints : 0,
    finished: Boolean(s.finished),
  };
}
